import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL 

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds timeout for code execution
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authentication token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Response Error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    return Promise.reject(error);
  }
);

// Generic API call function
export const apiCall = async (endpoint, options = {}) => {
  try {
    const response = await api({
      url: endpoint,
      method: options.method || 'GET',
      data: options.data,
      ...options
    });
    return response.data;
  } catch (error) {
    console.error(`API call failed for ${endpoint}:`, error);
    throw error.response?.data || error.message;
  }
};

// Course API calls
export const courseApi = {
  // Get all courses
  getAllCourses: async () => {
    try {
      const response = await api.get('/courses');
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get course by slug
  getCourseBySlug: async (slug) => {
    try {
      const response = await api.get(`/courses/slug/${slug}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get lessons by language
  getLessonsByLanguage: async (language) => {
    try {
      const response = await api.get(`/courses/language/${language}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get courses by level
  getCoursesByLevel: async (level) => {
    try {
      const response = await api.get(`/courses/level/${level}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Code execution API interface
export const codeApi = {
  /**
   * Execute code using the Piston API
   * @param {string} language - The programming language to execute (python, javascript, php)
   * @param {string} code - The source code to execute
   * @returns {Promise<{success: boolean, output: string, error: string|null}>}
   */
  validateCode(language, code) {
    // Common patterns to check for potentially unsafe or non-hardcoded inputs
    const unsafePatterns = {
      javascript: [
        { pattern: /\bprompt\b/, message: "Browser prompts are not allowed. Please use hardcoded values." },
        { pattern: /\bconfirm\s*\(/, message: "Browser confirmation dialogs are not allowed. Please use hardcoded values." },
        { pattern: /\breadline\s*\(/, message: "Interactive input is not allowed. Please use hardcoded values." },
        { pattern: /\bprocess\.stdin\b/, message: "Reading from stdin is not allowed. Please use hardcoded values." },
        { pattern: /\brequire\b|\bimport\b/, message: "Importing modules is not allowed for security reasons." }
      ],
      python: [
        { pattern: /\binput\b/, message: "Input function is not allowed. Please use hardcoded values." },
        { pattern: /\bopen\b/, message: "File operations are not allowed for security reasons." },
        { pattern: /\bimport\s+(?!math|random|datetime)/, message: "Only basic math, random, and datetime imports are allowed." }
      ],
      php: [
        { pattern: /\breadline\s*\(/, message: "Interactive input is not allowed. Please use hardcoded values." },
        { pattern: /\bfgets\s*\(\s*STDIN\s*\)/, message: "Reading from stdin is not allowed. Please use hardcoded values." },
        { pattern: /\bSTDIN\b|\$argv\b/, message: "Reading user input is not allowed. Please use hardcoded values." },
        { pattern: /\b(shell_exec|exec|passthru|system|proc_open|popen)\s*\(/, message: "Shell execution is not allowed." },
        { pattern: /\b(file_get_contents|fopen|fwrite|unlink|rename|copy|mkdir|rmdir)\s*\(/, message: "File operations are not allowed." },
        { pattern: /\b(curl_exec|fsockopen|pfsockopen)\s*\(/, message: "Network operations are not allowed." }
      ]
    };

    const patterns = unsafePatterns[language] || [];
    for (const { pattern, message } of patterns) {
      if (pattern.test(code)) {
        return { isValid: false, error: message };
      }
    }
    return { isValid: true };
  },

  async executeCode(language, code) {
    try {
      // Validate code first
      const validation = this.validateCode(language, code);
      if (!validation.isValid) {
        return {
          success: false,
          output: '',
          error: validation.error
        };
      }

      // Decode HTML entities in the code
      const decodedCode = code.replace(/&lt;/g, '<')
                             .replace(/&gt;/g, '>')
                             .replace(/&amp;/g, '&')
                             .replace(/&quot;/g, '"')
                             .replace(/&#39;/g, "'");

      const response = await api.post('/compiler/execute', {
        language,
        code: decodedCode,
      });

      const data = response.data;
      return {
        success: true,
        output: data.output,
        error: data.error || null,
      };
    } catch (error) {
      console.error(`Error executing ${language} code:`, error);
      return {
        success: false,
        output: '',
        error: error.response?.data?.error || error.message || `Error executing ${language} code`,
      };
    }
  },

  /**
   * Test API connection
   * @returns {Promise<object>}
   */
  async testConnection() {
    try {
      const response = await api.get('/test');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Error testing connection');
    }
  }
};

export default api;
